import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@design-system/Card';
import { List, ListItem } from '@design-system/List';
import { NavBar } from '@design-system/NavBar';
import { ErrorLoadingPage } from '@/components/screens/ErrorHandling/ErrorLoadingPage';
import { useSMPData } from './hooks/useSMPData';
import { useSMPNavigate } from './hooks/useSMPNavigate';
import { useGetOrderHistory } from '@viome/viome-base/src/usecase/SMP/SMPQuery';
import type { SMPOrder, SMPLacoreOrder } from '@viome/viome-base/src/usecase/SMP/SMP.model';
import type { AddressInfo } from '@viome/viome-base/src/usecase/OrderTracking/OrderTracking.model';
import {
  formatSMPDateFull,
  formatPrice,
  formatOrderAddress,
  getTrackingUrl,
} from './utils/smpHelpers';
import { Skeleton } from '@design-system/Skeleton';
import { CircularProgress } from '@design-system/CircularProgress';
import { ORDER_STATE_PROGRESS, getOrderStatusText, ShimmerImage } from './components';
import {
  useOrderTrackingViewModel,
  useOrderDetailViewModel,
} from '@/features/OrderTrackingViewModels';
import { cn } from '@/utils/styles';

const CONTACT_SUPPORT_URL = 'https://www.viome.com/contact';

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <h2 className="typography-h2 text-primary px-1">{children}</h2>
);

const InfoRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <ListItem hideIndicator>
    <span className="typography-body text-primary flex-1">{label}</span>
    <span className="typography-caption1 text-tertiary">{value}</span>
  </ListItem>
);

const NeedHelpSection = () => {
  const [isOpening, setIsOpening] = useState(false);

  const handleContactSupport = () => {
    setIsOpening(true);
    window.open(CONTACT_SUPPORT_URL, '_blank');
    setTimeout(() => setIsOpening(false), 3000);
  };

  return (
    <div className="flex flex-col gap-6">
      <SectionTitle>Need Help?</SectionTitle>

      <Card>
        <CardContent className="p-0">
          <List>
            <ListItem
              onClick={handleContactSupport}
              className="cursor-pointer"
            >
              Contact Support
              {isOpening && (
                <CircularProgress className="ml-auto size-5 text-tertiary" />
              )}
            </ListItem>
          </List>
        </CardContent>
      </Card>
    </div>
  );
};

// ---------------------------------------------------------------------------
// New API components (used by /smp/order-details)
// ---------------------------------------------------------------------------

const NewAddressBlock = ({
  address,
}: {
  address: AddressInfo | null;
}) => {
  if (!address) return <p className="text-tertiary">—</p>;

  return (
    <div className="flex flex-col typography-body text-tertiary fs-mask">
      {address.full_name && <span>{address.full_name}</span>}
      {address.address_line1 && <span>{address.address_line1}</span>}
      {address.address_line2 && <span>{address.address_line2}</span>}
      {(address.city || address.state || address.zip) && (
        <span>
          {address.city}, {address.state} {address.zip}
        </span>
      )}
      {address.country && <span>{address.country}</span>}
    </div>
  );
};

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatCurrency = (amount: number | null | undefined): string => {
  if (!amount) return '—';
  return `$${amount.toFixed(2)}`;
};

const SMPOrderDetailsNewAPI = () => {
  const { smpGoBack } = useSMPNavigate();
  const goBack = () => smpGoBack();
  const {
    isLoading: isTrackingLoading,
    isError: isTrackingError,
    hasOrder,
    progressPercentage,
    stageLabels,
    activeStep,
    orderInfo,
  } = useOrderTrackingViewModel();

  const orderNumber = orderInfo?.shopify_order_number ?? '';

  const {
    orderDetails,
    isOrderDetailsLoading,
    isOrderDetailsError,
  } = useOrderDetailViewModel(orderNumber);

  const isLoading = isTrackingLoading || isOrderDetailsLoading;

  if (isLoading) {
    return (
      <section
        className="page-container flex flex-col gap-10 pb-24"
        data-testid="smp-orderDetails-skeleton"
      >
        <NavBar returnButtonTitle="Back" className="md:-ml-9" onReturnClick={goBack} />
        <Skeleton className="h-8 w-48" />
        <div className="flex flex-col gap-6">
          <Skeleton className="h-48 w-full rounded-xl" />
          <Skeleton className="h-48 w-full rounded-xl" />
          <Skeleton className="h-48 w-full rounded-xl" />
        </div>
      </section>
    );
  }

  if (isTrackingError || isOrderDetailsError || !hasOrder) {
    return (
      <ErrorLoadingPage
        variant="light"
        errorType="error"
        hideBackground
        hideNavBar
      />
    );
  }

  const {
    shipping_information,
    order_information,
    payment_information,
    transaction_details,
  } = orderDetails ?? {};

  return (
    <section
      className="page-container flex flex-col gap-12 pb-24"
      data-testid="smp-orderDetailsPage"
    >
      <div className="flex flex-col gap-6">
        <NavBar returnButtonTitle="Back" className="md:-ml-9" onReturnClick={goBack} />

        <h1
          className="typography-h6 text-primary"
          data-testid="smp-orderDetails-title"
        >
          Order Details
        </h1>

        <Card
          className="border-0 bg-background-blur-bold"
          data-testid="smp-orderDetails-trackingCard"
        >
          <CardContent className="flex flex-col gap-4 p-6">
            <span className="typography-h3 text-[#146464] mb-4">
              {activeStep?.header_text ?? 'Order Status'}
            </span>

            <div className="w-full h-1 bg-background-secondary rounded-full relative">
              <div
                className="h-full bg-background-success transition-all duration-300 ease-in-out"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>

            <div className="flex justify-between">
              {stageLabels.map((label) => (
                <span
                  key={label}
                  className={cn(
                    'typography-caption2',
                    activeStep?.smp_status === label
                      ? 'text-primary font-medium'
                      : 'text-secondary',
                  )}
                >
                  {label}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {shipping_information && (
        <div className="flex flex-col gap-6">
          <SectionTitle>Shipping Information</SectionTitle>
          <Card>
            <CardContent className="py-5 px-5">
              <div className="flex flex-col gap-3">
                <span className="typography-body text-primary">
                  Shipping Address
                </span>
                <NewAddressBlock address={shipping_information} />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {order_information && (
        <div className="flex flex-col gap-6">
          <SectionTitle>Order Information</SectionTitle>
          <Card>
            <CardContent className="p-0">
              <List>
                <InfoRow
                  label="Date"
                  value={formatDate(order_information.order_date)}
                />
                <InfoRow
                  label="Order Number"
                  value={`#${order_information.order_number}`}
                />
                <InfoRow
                  label="Total"
                  value={formatCurrency(order_information.order_total)}
                />
              </List>
            </CardContent>
          </Card>
        </div>
      )}

      {payment_information && (
        <div className="flex flex-col gap-6">
          <SectionTitle>Payment Information</SectionTitle>
          <Card>
            <CardContent className="py-5 px-5">
              <div className="flex flex-col gap-3">
                <span className="typography-body text-primary">
                  Billing Address
                </span>
                <NewAddressBlock address={payment_information} />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {transaction_details && (
        <div className="flex flex-col gap-6">
          <SectionTitle>Transaction Details</SectionTitle>
          <Card>
            <CardContent className="p-0">
              <List>
                <InfoRow
                  label="Price"
                  value={formatCurrency(transaction_details.overall_price)}
                />
                <InfoRow
                  label="Tax"
                  value={formatCurrency(transaction_details.total_tax)}
                />
                {transaction_details.total_discounts != null &&
                  transaction_details.total_discounts > 0 && (
                    <InfoRow
                      label="Discount"
                      value={`-${formatCurrency(transaction_details.total_discounts)}`}
                    />
                  )}
                <InfoRow
                  label="Order Total"
                  value={formatCurrency(transaction_details.final_price)}
                />
              </List>
            </CardContent>
          </Card>
        </div>
      )}

      <NeedHelpSection />
    </section>
  );
};

// ---------------------------------------------------------------------------
// Old API components (used by /smp/order-history/:orderId)
// ---------------------------------------------------------------------------

const OldAddressBlock = ({ lines }: { lines: string[] }) => (
  <div className="flex flex-col typography-body text-tertiary fs-mask">
    {lines.map((line) => (
      <span key={line}>{line}</span>
    ))}
  </div>
);

const OrderTrackingCard = ({
  lacoreOrder,
  planName,
  productImage,
}: {
  lacoreOrder: SMPLacoreOrder;
  planName?: string;
  productImage?: string;
}) => {
  const progressPercent = ORDER_STATE_PROGRESS[lacoreOrder.current_state] ?? 0;

  return (
    <Card className="pb-6" data-testid="smp-orderDetails-trackingCard">
      <CardHeader>
        <CardTitle className="typography-h2 text-primary">
          Order in Progress
        </CardTitle>
      </CardHeader>

      <CardContent className="flex flex-col gap-6">
        <span className="typography-h3 text-primary">
          {getOrderStatusText(lacoreOrder)}
        </span>

        <div className="flex flex-col gap-3">
          <div className="relative w-full py-2">
            <div className="w-full h-1 rounded-full bg-stroke-secondary-subtle" />
            <div
              className="absolute top-1/2 -translate-y-1/2 left-0 h-1 rounded-full bg-success transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          <div className="flex justify-between">
            {lacoreOrder.available_states.map((state) => (
              <span key={state} className="typography-caption2 text-secondary">
                {state}
              </span>
            ))}
          </div>
        </div>

        {planName && (
          <div className="flex items-center gap-5">
            {productImage && (
              <ShimmerImage src={productImage} alt={planName} containerClassName="size-[72px] rounded-xl overflow-hidden bg-background shrink-0" className="size-full object-cover" />
            )}
            <span className="typography-body text-primary">{planName}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const DeliveredCard = ({
  planName,
  productImage,
  deliveryDate,
}: {
  planName?: string;
  productImage?: string;
  deliveryDate?: string;
}) => (
  <Card className="pb-6" data-testid="smp-orderDetails-deliveredCard">
    <CardHeader>
      <CardTitle className="typography-h2 text-primary">
        Order Delivered
      </CardTitle>
    </CardHeader>

    <CardContent className="flex flex-col gap-6">
      <span className="typography-h3 text-primary">
        Your order was ordered
        {deliveryDate ? ` on ${formatSMPDateFull(deliveryDate)}` : ''}.
      </span>

      {planName && (
        <div className="flex items-center gap-5">
          {productImage && (
            <ShimmerImage src={productImage} alt={planName} containerClassName="size-[72px] rounded-[12px] overflow-hidden flex items-center justify-center shrink-0" className="size-full object-cover" />
          )}
          <span className="typography-body text-primary">{planName}</span>
        </div>
      )}
    </CardContent>
  </Card>
);

const SMPOrderDetailsOldAPI = ({ orderId }: { orderId: string }) => {
  const { smpGoBack } = useSMPNavigate();
  const goBack = () => smpGoBack();
  const { subscriptionId, subscriptionDetail, isSessionReady, isLoading: isSMPLoading, isError: isSMPError } = useSMPData({
    skip: { discounts: true, changePlan: true, uiMessages: true, canSkip: true, holdingPattern: true },
  });

  const {
    data: orders,
    isLoading: isLoadingOrders,
    isError: isErrorOrders,
  } = useGetOrderHistory(subscriptionId, {
    enabled: isSessionReady && !!subscriptionId,
  });

  const isLoading = isSMPLoading || isLoadingOrders;
  const isError = isSMPError || isErrorOrders;

  if (isLoading) {
    return (
      <section className="page-container flex flex-col gap-10 pb-24">
        <NavBar returnButtonTitle="Back" className="md:-ml-9" onReturnClick={goBack} />
        <Skeleton className="h-8 w-48" />
        <div className="flex flex-col gap-6">
          <Skeleton className="h-64 w-full rounded-xl" />
          <Skeleton className="h-48 w-full rounded-xl" />
          <Skeleton className="h-48 w-full rounded-xl" />
        </div>
      </section>
    );
  }

  if (isError || !subscriptionDetail) {
    return (
      <ErrorLoadingPage
        variant="light"
        errorType="error"
        hideBackground
        hideNavBar
      />
    );
  }

  const currentOrder: SMPOrder | undefined = orders?.find(
    (o) => o.id === Number(orderId),
  );

  const lacoreOrder = currentOrder?.lacore_order ?? subscriptionDetail.lacore_order;
  const isDelivered =
    lacoreOrder?.current_state === 'Delivered' || !lacoreOrder;

  const firstLineItem = currentOrder?.line_items?.[0];
  const planName = firstLineItem?.title || firstLineItem?.shopify_product?.title;
  const productImage = firstLineItem?.shopify_product?.image;

  const ShippingSection = () => {
    if (!currentOrder) return null;
    const addressLines = formatOrderAddress(currentOrder.shipping_address);
    const trackingId = currentOrder.lacore_order?.tracking_id;
    const trackingUrl = trackingId ? getTrackingUrl(trackingId) : null;

    return (
      <div className="flex flex-col gap-6">
        <SectionTitle>Shipping Information</SectionTitle>
        <div className="flex flex-col gap-2">
          <Card>
            <CardContent className="py-5 px-5">
              <div className="flex flex-col gap-3">
                <span className="typography-body text-primary">
                  Shipping Address
                </span>
                <OldAddressBlock lines={addressLines} />
              </div>
            </CardContent>
          </Card>
          {(trackingId || trackingUrl) && (
            <Card>
              <CardContent className="p-0">
                <List>
                  {trackingId && (
                    <InfoRow label="Tracking ID" value={trackingId} />
                  )}
                  {trackingUrl && (
                    <ListItem asChild>
                      <a href={trackingUrl} target="_blank" rel="noreferrer">
                        Track Shipment
                      </a>
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  };

  return (
    <section
      className="page-container flex flex-col gap-12 pb-24"
      data-testid="smp-orderDetailsPage"
    >
      <div className="flex flex-col gap-6">
        <NavBar returnButtonTitle="Back" className="md:-ml-9" onReturnClick={goBack} />

        <h1
          className="typography-h6 text-primary"
          data-testid="smp-orderDetails-title"
        >
          Order Details
        </h1>

        {isDelivered ? (
          <DeliveredCard
            planName={planName}
            productImage={productImage}
            deliveryDate={lacoreOrder?.delivery_date || currentOrder?.order_date}
          />
        ) : (
          lacoreOrder && (
            <OrderTrackingCard
              lacoreOrder={lacoreOrder}
              planName={planName}
              productImage={productImage}
            />
          )
        )}
      </div>

      {currentOrder ? (
        <>
          <ShippingSection />
          <div className="flex flex-col gap-6">
            <SectionTitle>Order Information</SectionTitle>
            <Card>
              <CardContent className="p-0">
                <List>
                  <InfoRow
                    label="Date"
                    value={formatSMPDateFull(currentOrder.order_date)}
                  />
                  <InfoRow
                    label="Order Number"
                    value={`#${currentOrder.shopify_order_number}`}
                  />
                  <InfoRow
                    label="Total"
                    value={formatPrice(currentOrder.total_price)}
                  />
                </List>
              </CardContent>
            </Card>
          </div>
          <div className="flex flex-col gap-6">
            <SectionTitle>Payment Information</SectionTitle>
            <Card>
              <CardContent className="py-5 px-5">
                <div className="flex flex-col gap-3">
                  <span className="typography-body text-primary">
                    Billing Address
                  </span>
                  <OldAddressBlock
                    lines={formatOrderAddress(currentOrder.billing_address)}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="flex flex-col gap-6">
            <SectionTitle>Transaction Details</SectionTitle>
            <Card>
              <CardContent className="p-0">
                <List>
                  <InfoRow
                    label="Price"
                    value={formatPrice(
                      currentOrder.subtotal_price || currentOrder.first_item_price,
                    )}
                  />
                  <InfoRow
                    label="Tax"
                    value={formatPrice(currentOrder.total_tax)}
                  />
                  <InfoRow
                    label="Discount"
                    value={
                      currentOrder.total_discounts > 0
                        ? `-${formatPrice(currentOrder.total_discounts)}`
                        : formatPrice(0)
                    }
                  />
                  <InfoRow
                    label="Order Total"
                    value={formatPrice(currentOrder.total_price)}
                  />
                </List>
              </CardContent>
            </Card>
          </div>
        </>
      ) : (
        <div className="flex flex-col gap-6">
          <SectionTitle>Order Information</SectionTitle>
          <Card>
            <CardContent className="py-5 px-5">
              <span className="typography-body text-secondary">
                No order details available yet.
              </span>
            </CardContent>
          </Card>
        </div>
      )}

      <NeedHelpSection />
    </section>
  );
};

// ---------------------------------------------------------------------------
// Main export — routes to new or old API based on URL params
// ---------------------------------------------------------------------------

export const SMPOrderDetailsPage = () => {
  const { orderId } = useParams<{ orderId?: string }>();

  if (orderId) {
    return <SMPOrderDetailsOldAPI orderId={orderId} />;
  }

  return <SMPOrderDetailsNewAPI />;
};
